import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';

const KeysManagement = () => {
  const { user } = useAuth();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [revokeReason, setRevokeReason] = useState('');
  const [bulkDeleteReason, setBulkDeleteReason] = useState('');
  const { success, error } = useNotification();

  const fetchKeys = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/admin/keys', {
        params: {
          page: currentPage,
          limit: 15,
          search: searchTerm,
          status: statusFilter
        }
      });
      setKeys(response.data.keys);
      setTotalPages(response.data.totalPages);
    } catch (err) {
      console.error('Failed to fetch keys:', err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, statusFilter]);

  useEffect(() => {
    fetchKeys();
  }, [fetchKeys]);

  const handleRevokeKey = async (e) => {
    e.preventDefault();
    
    if (!revokeReason.trim()) {
      error('Please provide a reason for revoking this key');
      return;
    }

    try {
      const response = await axios.put(`/api/keys/${selectedKey._id}/revoke`, {
        reason: revokeReason.trim()
      });

      // Оновити статус ключа в локальному списку
      setKeys(prevKeys => 
        prevKeys.map(key => 
          key._id === selectedKey._id 
            ? { ...key, status: 'revoked', revokeReason: revokeReason.trim() }
            : key
        )
      );

      // Показати успішне повідомлення (зеленим кольором)
      success(`License key successfully revoked!\nKey: ${selectedKey.key}\nReason: ${revokeReason.trim()}`);
      
      // Закрити модальне вікно та очистити форму
      setShowRevokeModal(false);
      setSelectedKey(null);
      setRevokeReason('');
      
      // Оновити список ключів з сервера
      fetchKeys();
    } catch (err) {
      console.error('Revoke key error:', err);
      error(err.response?.data?.message || 'Failed to revoke license key');
    }
  };

  const handleDeleteKey = async () => {
    if (!selectedKey) return;

    try {
      await axios.delete(`/api/admin/keys/${selectedKey._id}`);
      
      success('License key deleted successfully!');
      setShowDeleteModal(false);
      setSelectedKey(null);
      fetchKeys();
    } catch (err) {
      error(err.response?.data?.message || 'Failed to delete license key');
    }
  };

  const handleSelectKey = (keyId) => {
    setSelectedKeys(prev => 
      prev.includes(keyId) 
        ? prev.filter(id => id !== keyId)
        : [...prev, keyId]
    );
  };

  const handleSelectAll = () => {
    if (selectedKeys.length === keys.length) {
      setSelectedKeys([]);
    } else {
      setSelectedKeys(keys.map(key => key._id));
    }
  };

  const handleBulkDelete = async (e) => {
    e.preventDefault();
    if (selectedKeys.length === 0 || !bulkDeleteReason.trim()) return;

    try {
      await axios.delete('/api/admin/keys/bulk', {
        data: {
          keyIds: selectedKeys,
          reason: bulkDeleteReason.trim()
        }
      });
      
      success(`Successfully deleted ${selectedKeys.length} license keys!`);
      setShowBulkDeleteModal(false);
      setSelectedKeys([]);
      setBulkDeleteReason('');
      fetchKeys();
    } catch (err) {
      error(err.response?.data?.message || 'Failed to delete license keys');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'available':
        return 'text-green-400 bg-green-600/20';
      case 'used':
        return 'text-blue-400 bg-blue-600/20';
      case 'revoked':
        return 'text-red-400 bg-red-600/20';
      case 'expired':
        return 'text-yellow-400 bg-yellow-600/20';
      default:
        return 'text-gray-400 bg-gray-600/20';
    }
  };

  const getStatusDetails = (licenseKey) => {
    const now = new Date();
    let status = licenseKey.status;
    let details = {};

    if (licenseKey.product?.duration > 0 && licenseKey.purchasedAt) {
      const expiryDate = new Date(licenseKey.purchasedAt);
      expiryDate.setDate(expiryDate.getDate() + licenseKey.product.duration);
      
      if (now > expiryDate && status !== 'expired' && status !== 'revoked') {
        status = 'expired';
      }
      
      details.expiryDate = expiryDate;
      details.daysRemaining = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));
    }

    details.activations = licenseKey.activations || [];
    details.maxActivations = licenseKey.product?.maxActivations || 1;
    details.isFullyActivated = details.activations.length >= details.maxActivations;

    return { status, details };
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    success('License key copied to clipboard!', 2000);
  };

  const formatLastUsed = (date) => {
    if (!date) return 'Never';
    const now = new Date();
    const lastUsed = new Date(date);
    const diffMs = now - lastUsed;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const KeyCard = ({ licenseKey }) => {
    const { status, details } = getStatusDetails(licenseKey);
    const isExpiringSoon = details.daysRemaining > 0 && details.daysRemaining <= 7;
    
    return (
      <div className={`card-astral p-6 hover:glow-purple transition-all duration-300 ${
        selectedKeys.includes(licenseKey._id) ? 'ring-2 ring-purple-500' : ''
      }`}>
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={selectedKeys.includes(licenseKey._id)}
              onChange={() => handleSelectKey(licenseKey._id)}
              className="mr-2"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-lg font-bold text-white">{licenseKey.product?.name}</h3>
                <span className="px-2 py-1 bg-purple-600/20 text-purple-400 text-xs rounded font-mono">
                  {licenseKey.product?.keyTag}
                </span>
              </div>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <span className={`px-2 py-1 text-xs rounded-full capitalize ${getStatusColor(status)}`}>
              {status}
            </span>
            {isExpiringSoon && (
              <span className="px-2 py-1 bg-orange-600/20 text-orange-400 text-xs rounded">
                {details.daysRemaining}d left
              </span>
            )}
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between bg-dark-200 p-3 rounded-lg">
            <code className="text-purple-400 font-mono text-sm flex-1">
              {licenseKey.key}
            </code>
            <button
              onClick={() => copyToClipboard(licenseKey.key)}
              className="text-gray-400 hover:text-purple-400 transition-colors ml-2"
              title="Copy to clipboard"
            >
              📋
            </button>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="bg-dark-200 p-2 rounded text-center">
              <div className="text-xs text-gray-400">Activations</div>
              <div className={`text-sm font-semibold ${
                details.isFullyActivated ? 'text-red-400' : 'text-blue-400'
              }`}>
                {details.activations.length} / {details.maxActivations}
              </div>
            </div>
            <div className="bg-dark-200 p-2 rounded text-center">
              <div className="text-xs text-gray-400">Last Used</div>
              <div className="text-sm font-semibold text-white">
                {details.activations.length > 0 
                  ? formatLastUsed(Math.max(...details.activations.map(a => new Date(a.lastUsed))))
                  : 'Never'
                }
              </div>
            </div>
          </div>

          {status === 'expired' && details.expiryDate && (
            <div className="bg-yellow-600/20 border border-yellow-500/50 rounded p-2">
              <div className="text-yellow-400 text-xs">
                ⏰ Expired on {details.expiryDate.toLocaleDateString()}
              </div>
            </div>
          )}

          {status === 'revoked' && licenseKey.revokeReason && (
            <div className="bg-red-600/20 border border-red-500/50 rounded p-2">
              <div className="text-red-400 text-xs">
                🚫 {licenseKey.revokeReason}
              </div>
            </div>
          )}

          {details.activations.length > 0 && (
            <div className="bg-blue-600/20 border border-blue-500/30 rounded p-2">
              <div className="text-blue-400 text-xs font-semibold mb-1">
                Active Devices ({details.activations.length})
              </div>
              {details.activations.slice(0, 2).map((activation, index) => (
                <div key={index} className="text-xs text-gray-300">
                  📱 {activation.deviceInfo?.manufacturer} {activation.deviceInfo?.model}
                </div>
              ))}
              {details.activations.length > 2 && (
                <div className="text-xs text-gray-400">
                  +{details.activations.length - 2} more devices
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Created:</span>
              <div className="text-white">
                {new Date(licenseKey.createdAt).toLocaleDateString()}
              </div>
            </div>
            <div>
              <span className="text-gray-500">Method:</span>
              <div className="text-white capitalize">
                {licenseKey.metadata?.generationMethod || 'automatic'}
              </div>
            </div>
            {licenseKey.purchasedAt && (
              <div>
                <span className="text-gray-500">Purchased:</span>
                <div className="text-white">
                  {new Date(licenseKey.purchasedAt).toLocaleDateString()}
                </div>
              </div>
            )}
            {licenseKey.purchasePrice && (
              <div>
                <span className="text-gray-500">Price:</span>
                <div className="text-green-400">
                  ${licenseKey.purchasePrice}
                </div>
              </div>
            )}
          </div>

          <div className="text-xs text-gray-500 border-t border-gray-700 pt-2">
            <div>Created by: {licenseKey.createdBy?.username}</div>
            {licenseKey.purchasedBy && (
              <div>Purchased by: {licenseKey.purchasedBy?.username}</div>
            )}
          </div>

          <div className="flex space-x-2">
            <button
              onClick={() => {
                setSelectedKey(licenseKey);
                setShowDetailModal(true);
              }}
              className="flex-1 btn-neon text-xs py-2"
            >
              Details
            </button>
            {licenseKey.status !== 'revoked' && user?.role === 'owner' && (
              <button
                onClick={() => {
                  setSelectedKey(licenseKey);
                  setShowRevokeModal(true);
                }}
                className="flex-1 btn-neon-blue text-xs py-2"
              >
                Revoke
              </button>
            )}
            {user?.role === 'owner' && (
              <button
                onClick={() => {
                  setSelectedKey(licenseKey);
                  setShowDeleteModal(true);
                }}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white text-xs py-2 px-3 rounded transition-colors"
              >
                Delete
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const KeyDetailView = ({ licenseKey }) => {
    const { status, details } = getStatusDetails(licenseKey);
    const activations = licenseKey.activations || [];
    const isExpiringSoon = details.daysRemaining > 0 && details.daysRemaining <= 7;
    
    return (
      <div className="space-y-4">
        <div className="bg-dark-200 p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-white mb-3">Device Binding Information</h3>
          
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-400">Max Devices:</span>
              <span className="text-white">{licenseKey.maxActivations || licenseKey.product?.maxActivations || 1}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Bound Devices:</span>
              <span className={`${
                activations.length >= (licenseKey.maxActivations || licenseKey.product?.maxActivations || 1)
                  ? 'text-red-400'
                  : 'text-green-400'
              } font-semibold`}>
                {activations.length} / {licenseKey.maxActivations || licenseKey.product?.maxActivations || 1}
              </span>
            </div>
            
            {activations.length === 0 ? (
              <div className="text-center p-3 bg-blue-600/10 rounded-lg mt-2">
                <span className="text-blue-400">
                  This key is not bound to any device yet
                </span>
              </div>
            ) : (
              <div className="mt-2">
                <h4 className="text-sm font-semibold text-gray-300 mb-2">Bound Devices:</h4>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {activations.map((activation, idx) => (
                    <div key={idx} className="bg-dark-100 p-2 rounded-lg border border-gray-700">
                      <div className="flex justify-between mb-1">
                        <span className="text-white text-sm font-semibold">
                          Device #{idx + 1}
                        </span>
                        <span className="text-xs text-gray-400">
                          {new Date(activation.activatedAt).toLocaleDateString()}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-xs">
                        <div>
                          <span className="text-gray-400">Model:</span> 
                          <span className="text-white ml-1">{activation.deviceInfo?.model || 'Unknown'}</span>
                        </div>
                        <div>
                          <span className="text-gray-400">Manufacturer:</span> 
                          <span className="text-white ml-1">{activation.deviceInfo?.manufacturer || 'Unknown'}</span>
                        </div>
                        <div>
                          <span className="text-gray-400">Last Used:</span> 
                          <span className="text-white ml-1">{formatLastUsed(activation.lastUsed)}</span>
                        </div>
                        <div>
                          <span className="text-gray-400">Device:</span> 
                          <span className="text-white ml-1">{activation.deviceInfo?.device || 'Unknown'}</span>
                        </div>
                      </div>
                      
                      {activation.deviceFingerprint && (
                        <div className="mt-1 text-xs">
                          <span className="text-gray-400">Fingerprint:</span>
                          <code className="text-purple-400 font-mono text-xs block mt-0.5 break-all">
                            {activation.deviceFingerprint.substring(0, 16)}...
                          </code>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        
        <div className="bg-dark-200 p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-white mb-3">License Validity</h3>
          
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-400">Status:</span>
              <span className={`px-2 py-1 text-xs rounded-full capitalize ${getStatusColor(status)}`}>
                {status}
              </span>
            </div>
            
            {licenseKey.duration && (
              <div className="flex justify-between">
                <span className="text-gray-400">Duration:</span>
                <span className="text-white">{licenseKey.duration} days</span>
              </div>
            )}
            
            {licenseKey.purchasedAt && (
              <div className="flex justify-between">
                <span className="text-gray-400">Purchased:</span>
                <span className="text-white">{new Date(licenseKey.purchasedAt).toLocaleString()}</span>
              </div>
            )}
            
            {details.expiryDate && (
              <div className="flex justify-between">
                <span className="text-gray-400">Expires:</span>
                <span className={isExpiringSoon ? 'text-yellow-400' : 'text-white'}>
                  {details.expiryDate.toLocaleString()}
                </span>
              </div>
            )}
            
            {details.daysRemaining > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-400">Days Remaining:</span>
                <span className={
                  details.daysRemaining <= 3 ? 'text-red-400' :
                  details.daysRemaining <= 7 ? 'text-yellow-400' :
                  'text-green-400'
                }>
                  {details.daysRemaining} days
                </span>
              </div>
            )}
            
            {licenseKey.firstUsedAt && (
              <div className="flex justify-between">
                <span className="text-gray-400">First Used:</span>
                <span className="text-white">{new Date(licenseKey.firstUsedAt).toLocaleString()}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">
          {user?.role === 'reseller' ? 'My License Keys' : 'License Key Management'}
        </h2>
        <div className="flex items-center space-x-4">
          {selectedKeys.length > 0 && user?.role === 'owner' && (
            <button
              onClick={() => setShowBulkDeleteModal(true)}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded text-sm transition-colors"
            >
              Delete Selected ({selectedKeys.length})
            </button>
          )}
          <div className="text-sm text-gray-400">
            Total: {keys.length} keys
          </div>
        </div>
      </div>

      <div className="card-astral p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Search Keys
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by key or product tag..."
              className="w-full px-3 py-2 bg-dark-200 border border-purple-500/30 rounded text-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Filter by Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 bg-dark-200 border border-purple-500/30 rounded text-white"
            >
              <option value="">All Status</option>
              <option value="available">Available</option>
              <option value="used">Used</option>
              <option value="revoked">Revoked</option>
              <option value="expired">Expired</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('');
                setCurrentPage(1);
              }}
              className="btn-neon-blue w-full"
            >
              Clear Filters
            </button>
          </div>
          <div className="flex items-end">
            <button
              onClick={handleSelectAll}
              className="btn-neon w-full"
              disabled={user?.role === 'reseller'}
            >
              {selectedKeys.length === keys.length ? 'Deselect All' : 'Select All'}
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner"></div>
        </div>
      ) : keys.length === 0 ? (
        <div className="card-astral p-8 text-center">
          <h3 className="text-xl font-bold text-gray-400 mb-2">No Keys Found</h3>
          <p className="text-gray-500">No license keys match your current filters.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {keys.map((key) => (
            <KeyCard key={key._id} licenseKey={key} />
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex justify-center space-x-2">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="btn-neon-blue disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <span className="flex items-center px-4 py-2 text-white">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="btn-neon-blue disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}

      {showDetailModal && selectedKey && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="card-astral p-6 w-full max-w-4xl mx-4 max-h-screen overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white">License Key Details</h2>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column - Basic Info */}
              <div className="space-y-4">
                <div className="bg-dark-200 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-white mb-3">Basic Information</h3>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Product:</span>
                      <span className="text-white font-semibold">{selectedKey.product?.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Key Tag:</span>
                      <code className="text-purple-400 font-mono">{selectedKey.product?.keyTag}</code>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Status:</span>
                      <span className={`px-2 py-1 text-xs rounded-full capitalize ${getStatusColor(getStatusDetails(selectedKey).status)}`}>
                        {getStatusDetails(selectedKey).status}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">License Key:</span>
                      <div className="flex items-center gap-2">
                        <code className="text-purple-400 font-mono text-sm break-all">{selectedKey.key}</code>
                        <button
                          onClick={() => copyToClipboard(selectedKey.key)}
                          className="text-gray-400 hover:text-purple-400 transition-colors"
                        >
                          📋
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Enhanced Key Detail View */}
                <KeyDetailView licenseKey={selectedKey} />

                {/* Add other existing sections (Purchase Info, etc.) */}
                {/* ...existing code... */}
              </div>

              {/* Right Column - Device Activations & Usage Stats */}
              <div className="space-y-4">
                <div className="bg-dark-200 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-white mb-3">
                    Device Activations ({(selectedKey.activations || []).length} / {selectedKey.product?.maxActivations || 1})
                  </h3>
                  
                  {(!selectedKey.activations || selectedKey.activations.length === 0) ? (
                    <div className="text-center py-8">
                      <div className="text-gray-400 text-lg">📱</div>
                      <p className="text-gray-500 mt-2">No device activations yet</p>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {selectedKey.activations.map((activation, index) => (
                        <div key={index} className="bg-dark-100 p-3 rounded border-l-4 border-blue-500">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1">
                              <div className="font-semibold text-white">
                                Device #{index + 1}
                              </div>
                              <div className="text-sm text-gray-400">
                                {activation.deviceInfo?.manufacturer} {activation.deviceInfo?.model}
                              </div>
                            </div>
                            <div className="text-xs text-gray-500">
                              {formatLastUsed(activation.lastUsed)}
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-3 text-xs">
                            <div>
                              <span className="text-gray-500">Device:</span>
                              <div className="text-white">{activation.deviceInfo?.device || 'Unknown'}</div>
                            </div>
                            <div>
                              <span className="text-gray-500">CPU:</span>
                              <div className="text-white">{activation.deviceInfo?.cpuAbi || 'Unknown'}</div>
                            </div>
                            <div>
                              <span className="text-gray-500">Activated:</span>
                              <div className="text-white">{new Date(activation.activatedAt).toLocaleDateString()}</div>
                            </div>
                            <div>
                              <span className="text-gray-500">Last used:</span>
                              <div className="text-white">{new Date(activation.lastUsed).toLocaleDateString()}</div>
                            </div>
                          </div>
                          
                          {activation.deviceInfo?.fingerprint && (
                            <div className="mt-2 pt-2 border-t border-gray-600">
                              <span className="text-gray-500 text-xs">Fingerprint:</span>
                              <code className="text-purple-400 font-mono text-xs block mt-1 break-all">
                                {activation.deviceInfo.fingerprint}
                              </code>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {selectedKey.activations && selectedKey.activations.length > 0 && (
                  <div className="bg-dark-200 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-white mb-3">Usage Statistics</h3>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="text-center p-3 bg-blue-600/10 rounded">
                        <div className="text-blue-400 font-semibold text-lg">
                          {selectedKey.activations.length}
                        </div>
                        <div className="text-xs text-gray-400">Active Devices</div>
                      </div>
                      <div className="text-center p-3 bg-green-600/10 rounded">
                        <div className="text-green-400 font-semibold text-lg">
                          {(() => {
                            const lastUsed = Math.max(...selectedKey.activations.map(a => new Date(a.lastUsed)));
                            return formatLastUsed(lastUsed);
                          })()}
                        </div>
                        <div className="text-xs text-gray-400">Last Activity</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowDetailModal(false)}
                className="btn-neon-blue"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {user?.role === 'owner' && showBulkDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="card-astral p-6 w-full max-w-md mx-4">
            <h2 className="text-xl font-bold text-white mb-4">Delete Multiple License Keys</h2>
            
            <div className="bg-red-600/20 border border-red-500/50 rounded-lg p-3 mb-4">
              <p className="text-red-400 text-sm">
                ⚠️ You are about to delete {selectedKeys.length} license keys permanently.
              </p>
              <p className="text-red-400 text-sm mt-2">
                This action cannot be undone and may affect users who have purchased these keys.
              </p>
            </div>

            <form onSubmit={handleBulkDelete} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Reason for Bulk Deletion
                </label>
                <textarea
                  value={bulkDeleteReason}
                  onChange={(e) => setBulkDeleteReason(e.target.value)}
                  className="w-full px-3 py-2 bg-dark-200 border border-purple-500/30 rounded text-white"
                  rows="3"
                  placeholder="Enter reason for deleting these license keys..."
                  required
                />
              </div>
              
              <div className="bg-dark-200 p-3 rounded-lg">
                <p className="text-sm text-gray-400 mb-2">Selected keys by status:</p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {['available', 'used', 'revoked'].map(status => {
                    const count = keys.filter(key => 
                      selectedKeys.includes(key._id) && key.status === status
                    ).length;
                    if (count > 0) {
                      return (
                        <div key={status} className="flex justify-between">
                          <span className="text-gray-400 capitalize">{status}:</span>
                          <span className="text-white">{count}</span>
                        </div>
                      );
                    }
                    return null;
                  })}
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button type="submit" className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded transition-colors font-semibold">
                  Delete {selectedKeys.length} Keys
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowBulkDeleteModal(false);
                    setBulkDeleteReason('');
                  }}
                  className="btn-neon-blue flex-1"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {user?.role === 'owner' && showRevokeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="card-astral p-6 w-full max-w-md mx-4">
            <h2 className="text-xl font-bold text-white mb-4">Revoke License Key</h2>
            <div className="mb-4">
              <p className="text-gray-400 mb-2">Key: {selectedKey?.key}</p>
              <p className="text-gray-400">Product: {selectedKey?.product?.name}</p>
            </div>
            <form onSubmit={handleRevokeKey} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Reason for Revocation
                </label>
                <textarea
                  value={revokeReason}
                  onChange={(e) => setRevokeReason(e.target.value)}
                  className="w-full px-3 py-2 bg-dark-200 border border-purple-500/30 rounded text-white"
                  rows="3"
                  placeholder="Enter reason for revoking this license key..."
                  required
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button type="submit" className="btn-neon flex-1">
                  Revoke License
                </button>
                <button
                  type="button"
                  onClick={() => setShowRevokeModal(false)}
                  className="btn-neon-blue flex-1"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {user?.role === 'owner' && showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="card-astral p-6 w-full max-w-md mx-4">
            <h2 className="text-xl font-bold text-white mb-4">Delete License Key</h2>
            <div className="mb-4">
              <p className="text-gray-400 mb-2">
                Are you sure you want to delete this license key?
              </p>
              <div className="bg-dark-200 p-3 rounded-lg mb-3">
                <p className="text-sm text-gray-400 mb-1">Key:</p>
                <code className="text-purple-400 font-mono text-sm break-all">
                  {selectedKey?.key}
                </code>
                <p className="text-sm text-gray-400 mt-2 mb-1">Product:</p>
                <p className="text-white text-sm">{selectedKey?.product?.name}</p>
                <p className="text-sm text-gray-400 mt-2 mb-1">Status:</p>
                <span className={`px-2 py-1 text-xs rounded-full capitalize ${getStatusColor(selectedKey?.status)}`}>
                  {selectedKey?.status}
                </span>
              </div>
              
              <div className="bg-red-600/20 border border-red-500/50 rounded-lg p-3 mb-4">
                <p className="text-red-400 text-sm">
                  ⚠️ Warning: This action cannot be undone. The license key will be permanently removed from the system.
                </p>
                {selectedKey?.status === 'used' && (
                  <p className="text-red-400 text-sm mt-2">
                    🚨 This key is currently in use. Deleting it may affect the user who purchased it.
                  </p>
                )}
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={handleDeleteKey}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded transition-colors font-semibold"
              >
                Delete Permanently
              </button>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedKey(null);
                }}
                className="btn-neon-blue flex-1"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KeysManagement;
